import {
  Button,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EditIcon, EyeIcon, ExternalLinkIcon, SendIcon, TrashIcon } from "lucide-react";
import type { ReactNode } from "react";
import { formatDateTimeLocal } from "@/shared/lib/date-time-performance";
import { buildPerformanceRequestWhatsAppUrl } from "@/shared/lib/whatsapp";
import { RowActions } from "@/shared/ui/RowActions";
import { WhatsAppLinkButton } from "@/shared/ui/WhatsAppLinkButton";
import type { RequestDTO } from "../../domain/contracts";
import {
  isAwaitingConvert,
  isAwaitingConvertStale,
} from "../../domain/services/awaiting-convert";
import { eventFeaturesFromBillingMode } from "../../domain/services/event-features";
import { EVENT_KIND_LABELS } from "../lib/event-detail-ui";
import { EventFeatureChips } from "./EventFeatureChips";
import { EventProgressBar } from "./EventProgressBar";
import { PriorityOrderBadge } from "./PriorityOrderBadge";

type RequestRow = RequestDTO;

const STATUS_LABELS: Record<string, string> = {
  new: "ໃໝ່",
  negotiating: "ກຳລັງເຈລະຈາ",
  pending_quote: "ລໍຖ້າໃບສະເໜີລາຄາ",
  won: "ຢືນຢັນແລ້ວ",
  lost: "ເສຍໂອກາດ",
  cancelled: "ຍົກເລີກ",
};

const CHANNEL_LABELS: Record<string, string> = {
  phone: "ໂທລະສັບ",
  line: "LINE",
  facebook: "Facebook",
  walk_in: "ເຂົ້າມາຕິດຕໍ່",
  referral: "ແນະນຳ",
  other: "ອື່ນໆ",
};

const STATUS_COLORS: Record<string, string> = {
  new: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  negotiating:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  pending_quote:
    "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  won: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  lost: "bg-muted text-muted-foreground",
  cancelled: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

type RequestTableProps = {
  data: RequestRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView: (row: RequestRow) => void;
  onEdit: (row: RequestRow) => void;
  onDelete: (id: string) => void;
  canConvert?: boolean;
  onConvert?: (row: RequestRow) => void;
  onOpenEvent?: (row: RequestRow) => void;
};

function fmtDate(d: Date | string | null | undefined): string {
  if (!d) return "-";
  return formatDateTimeLocal(new Date(d));
}

function isConvertedEventComplete(request: RequestRow): boolean {
  return request.convertedEventProgress?.operationalPercent === 100;
}

function DimCell({
  request,
  children,
  className,
}: {
  request: RequestRow;
  children: ReactNode;
  className?: string;
}) {
  const dimmed = isConvertedEventComplete(request);
  return (
    <div
      className={[className, dimmed ? "opacity-40" : undefined]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </div>
  );
}

export function RequestTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onEdit,
  onDelete,
  canConvert = false,
  onConvert,
  onOpenEvent,
}: RequestTableProps) {
  const columns: TanstackReactTable.ColumnDef<RequestRow>[] = [
    {
      ...createSortableColumn("priorityOrder", "ຄວາມສຳຄັນ"),
      cell: ({ row }) => {
        const request = row.original;
        return (
          <DimCell
            request={request}
            className="flex flex-wrap items-center gap-1"
          >
            <PriorityOrderBadge priorityOrder={request.priorityOrder} />
            {request.convertedEventWasPostponed ||
            request.convertedEventDateTbd ? (
              <span
                className="inline-flex items-center rounded bg-amber-500 px-2 py-0.5 font-semibold text-xs text-white tracking-wide"
                title={
                  request.convertedEventDateTbd
                    ? "ງານທີ່ແປງແລ້ວເລື່ອນບໍ່ມີກຳນົດ"
                    : "ງານທີ່ແປງແລ້ວເຄີຍເລື່ອນວັນ"
                }
              >
                {request.convertedEventDateTbd ? "ຍັງບໍ່ກຳນົດ" : "ເລື່ອນ"}
              </span>
            ) : null}
          </DimCell>
        );
      },
    },
    {
      ...createSortableColumn("title", "ຄຳຂໍ"),
      cell: ({ row }) => (
        <DimCell request={row.original}>
          <p className="font-semibold text-sm leading-snug">
            {row.original.title}
          </p>
          <p className="mt-0.5 text-muted-foreground text-xs">
            {row.original.customerName || row.original.contactName}
          </p>
        </DimCell>
      ),
    },
    {
      id: "customerName",
      header: "ລູກຄ້າ",
      cell: ({ row }) => (
        <DimCell request={row.original} className="text-sm">
          <p className="font-medium">
            {row.original.customerName || "—"}
          </p>
          {row.original.customerName ? (
            <p className="text-muted-foreground text-xs">
              {row.original.contactName}
            </p>
          ) : null}
        </DimCell>
      ),
    },
    {
      id: "contactPhone",
      header: "ຕິດຕໍ່",
      cell: ({ row }) => {
        const phone = row.original.contactPhone;
        const waHref = phone
          ? buildPerformanceRequestWhatsAppUrl({
              phone,
              contactName: row.original.contactName,
              requestTitle: row.original.title,
            })
          : null;
        return (
          <DimCell request={row.original} className="text-sm">
            <div className="flex items-center gap-1.5">
              <p className="min-w-0">{phone ?? "-"}</p>
              <WhatsAppLinkButton phone={phone} href={waHref} iconOnly />
            </div>
            {row.original.contactEmail && (
              <p className="text-muted-foreground text-xs">
                {row.original.contactEmail}
              </p>
            )}
            {row.original.contactAddress && (
              <p className="line-clamp-1 text-muted-foreground text-xs">
                {row.original.contactAddress}
              </p>
            )}
          </DimCell>
        );
      },
    },
    {
      id: "channel",
      header: "ຊ່ອງທາງ",
      cell: ({ row }) => (
        <DimCell request={row.original} className="text-sm capitalize">
          {CHANNEL_LABELS[row.original.channel] ?? row.original.channel}
        </DimCell>
      ),
    },
    {
      id: "eventCategoryFeatures",
      header: "ຫມວດ / Features",
      cell: ({ row }) => {
        const request = row.original;
        if (!request.convertedEventId) {
          return (
            <DimCell request={request} className="text-muted-foreground text-sm">
              —
            </DimCell>
          );
        }
        const kind = request.convertedEventKind ?? "stage_show";
        const billingMode = request.convertedEventBillingMode ?? "performance";
        const features = eventFeaturesFromBillingMode(billingMode);
        return (
          <DimCell request={request}>
            <p className="font-medium text-sm">
              {EVENT_KIND_LABELS[kind] ?? kind}
            </p>
            <EventFeatureChips features={features} max={2} />
          </DimCell>
        );
      },
    },
    {
      id: "requestedEventDate",
      header: "ວັນທີ່ຕ້ອງການ",
      cell: ({ row }) => (
        <DimCell request={row.original} className="text-sm tabular-nums">
          {fmtDate(row.original.requestedEventDate)}
        </DimCell>
      ),
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => {
        const request = row.original;
        const s = request.status;
        const awaiting = isAwaitingConvert(request);
        const stale = isAwaitingConvertStale(request);
        return (
          <DimCell request={request} className="flex flex-wrap items-center gap-1">
            <span
              className={`inline-flex items-center rounded px-2 py-0.5 font-semibold text-xs tracking-wide ${STATUS_COLORS[s] ?? STATUS_COLORS.new}`}
            >
              {STATUS_LABELS[s] ?? s}
            </span>
            {awaiting ? (
              <span
                className={`inline-flex items-center rounded px-2 py-0.5 font-semibold text-xs tracking-wide ${
                  stale
                    ? "bg-amber-500 text-white"
                    : "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200"
                }`}
              >
                ລໍຖ້າແປງ
              </span>
            ) : null}
          </DimCell>
        );
      },
    },
    {
      id: "progress",
      header: "ຄວາມຄືບໜ້າ",
      cell: ({ row }) => (
        <DimCell request={row.original}>
          {row.original.convertedEventId ? (
            <EventProgressBar progress={row.original.convertedEventProgress} />
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          )}
        </DimCell>
      ),
    },
    {
      id: "assignedTo",
      header: "ຜູ້ຮັບຜິດຊອບ",
      cell: ({ row }) => (
        <DimCell request={row.original} className="text-sm">
          {row.original.assignedToName ?? row.original.assignedTo ?? "-"}
        </DimCell>
      ),
    },
    {
      id: "convertedEventId",
      header: "ງານສະແດງ",
      cell: ({ row }) => {
        const request = row.original;
        if (request.convertedEventId) {
          return (
            <DimCell request={request}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 max-w-[220px]"
                title={request.convertedEventTitle ?? undefined}
                onClick={() => onOpenEvent?.(request)}
              >
                <ExternalLinkIcon className="size-4 shrink-0" />
                <span className="truncate">
                  {request.convertedEventTitle ?? "ເປີດງານສະແດງ"}
                </span>
              </Button>
            </DimCell>
          );
        }
        if (canConvert && onConvert) {
          return (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => onConvert(request)}
            >
              <SendIcon className="size-4" />
              ສ້າງງານສະແດງ
            </Button>
          );
        }
        return <span className="text-muted-foreground text-sm">-</span>;
      },
    },
    {
      id: "actions",
      header: "ຈັດການ",
      cell: ({ row }: { row: { original: RequestRow } }) => {
        const request = row.original;
        const { id } = request;
        const actions = [
          {
            label: "ເບິ່ງ",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onView(request),
          },
          {
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(request),
          },
          {
            label: "ລຶບ",
            variant: "destructive" as const,
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບຄຳຂໍ",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບຄຳຂໍນີ້?",
                actionText: "ລຶບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) onDelete(id);
            },
          },
        ];
        return (
          <DimCell request={request}>
            <RowActions actions={actions} maxInline={3} />
          </DimCell>
        );
      },
    },
  ];

  return (
    <DataTable<RequestRow, unknown>
      noDataMessage="ບໍ່ພົບຄຳຂໍ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
